// src/oura/components/ScanAuditBadge.tsx
//
// The automatic audit result on a scan, for staff (#4242, ourteam-backend#5910).
// Green "Audit clean", amber "N issues", grey "Not audited" / "Audit error".
// Pressing it opens the list: issues grouped by question, each with its rule and
// detail and a way to the question's answer; the audit's notes sit apart under
// "Not failures". Staff only, and never mounted for a client.

import { useState } from "react";
import { ArrowRightIcon } from "lucide-react";
import { Drawer, DrawerBlock, FOCUS_RING, PillButton, TYPE, Tag } from "@oura/ui";
import { cn } from "../lib/cn";
import type { AeoScanAudit } from "@service/aeo";
import { useScanAudit } from "../hooks/useScanAudit";
import { auditBadge, groupByQuestion, usableIssues, usableNotes } from "../lib/scanAudit";

export function ScanAuditBadge({
    assessmentId,
    enabled,
    scanAt,
    questionText,
    onOpenQuestion,
}: {
    /** The scan on screen, latest or past. */
    assessmentId: string | undefined;
    /** Staff on a live business. Anything else and nothing is asked or drawn. */
    enabled: boolean;
    /** When the scan finished: a scan this fresh is asked about again until its audit lands. */
    scanAt?: string | null;
    /** The text of question `n`, or null when this scan does not carry that slot. */
    questionText?: (n: number) => string | null;
    /** Open that question's answer panel. Offered only for a question the scan carries. */
    onOpenQuestion?: (n: number) => void;
}) {
    const audit = useScanAudit(assessmentId, enabled, scanAt);
    if (!enabled || !audit) return null;
    return <ScanAuditView audit={audit} questionText={questionText} onOpenQuestion={onOpenQuestion} />;
}

/** The badge and its list for an audit already in hand. Split from the fetch so it can be rendered on its own. */
export function ScanAuditView({
    audit,
    questionText,
    onOpenQuestion,
}: {
    audit: AeoScanAudit;
    questionText?: (n: number) => string | null;
    onOpenQuestion?: (n: number) => void;
}) {
    const [open, setOpen] = useState(false);
    const badge = auditBadge(audit);
    const issues = groupByQuestion(usableIssues(audit));
    const notes = groupByQuestion(usableNotes(audit));
    const text = (n: number) => (n > 0 ? questionText?.(n) ?? null : null);
    const heading = (n: number, base: string) => {
        const t = text(n);
        return t ? `${base} · ${t}` : base;
    };
    const canOpen = (n: number) => n > 0 && !!onOpenQuestion && text(n) !== null;

    const tag = <Tag tone={badge.tone}>{badge.label}</Tag>;
    return (
        <>
            {/* The button only when there is a list behind it: a clean audit with
                nothing to say is a label, not a control that opens an empty box. */}
            {badge.hasList ? (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    title={badge.title || undefined}
                    aria-label={`${badge.label}. Open the audit.`}
                    className={cn("rounded-full", FOCUS_RING)}
                >
                    {tag}
                </button>
            ) : (
                <span title={badge.title || undefined}>{tag}</span>
            )}
            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                title="Scan audit"
                sub={badge.title || undefined}
                width={560}
            >
                <div className="space-y-4 p-1">
                    {issues.length === 0 && notes.length === 0 && (
                        <p className={cn(TYPE.tb, "m-0 text-gray-500")}>The audit has nothing to list for this scan.</p>
                    )}
                    {issues.map((g) => (
                        <DrawerBlock key={`i${g.n}`} label={heading(g.n, g.heading)}>
                            <ul className="m-0 list-none space-y-2 p-0">
                                {g.rows.map((r, i) => (
                                    <li key={i}>
                                        <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{r.rule}</p>
                                        {r.detail && <p className={cn(TYPE.tm, "m-0 text-gray-600")}>{r.detail}</p>}
                                    </li>
                                ))}
                            </ul>
                            {canOpen(g.n) && (
                                <PillButton
                                    size="sm"
                                    icon={ArrowRightIcon}
                                    className="mt-2"
                                    onClick={() => {
                                        setOpen(false);
                                        onOpenQuestion?.(g.n);
                                    }}
                                >
                                    Open the answer
                                </PillButton>
                            )}
                        </DrawerBlock>
                    ))}
                    {notes.length > 0 && (
                        <div>
                            <p className={cn(TYPE.lab, "m-0 mb-1")}>Not failures</p>
                            <ul className="m-0 list-none space-y-1 p-0">
                                {notes.flatMap((g) =>
                                    g.rows.map((r, i) => (
                                        <li key={`n${g.n}-${i}`} className={cn(TYPE.tm, "text-gray-500")}>
                                            <span className="tabular-nums text-gray-400">{g.n === 0 ? "Scan" : `Q${g.n}`}</span>
                                            {" · "}
                                            {r.note}
                                            {canOpen(g.n) && (
                                                <>
                                                    {" "}
                                                    <button
                                                        type="button"
                                                        className={cn("rounded text-blue-600 hover:underline", FOCUS_RING)}
                                                        onClick={() => {
                                                            setOpen(false);
                                                            onOpenQuestion?.(g.n);
                                                        }}
                                                    >
                                                        Open the answer
                                                    </button>
                                                </>
                                            )}
                                        </li>
                                    )),
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </Drawer>
        </>
    );
}
